import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import { cn } from "../../lib/utils";

/**
 * What an upload will take, said inside the empty drop area rather than in the
 * error the customer gets for guessing wrong.
 *
 * Every upload in the portal has an accepted-format list and most have a size
 * cap, and until now none of them said so anywhere: the first a customer heard
 * of either was a red toast after they had already picked the file. On the
 * claim pages that is worse than an inconvenience — each file costs a paid OCR
 * call, so a rejected pick is a wasted one.
 *
 * Small-caps and tracked rather than a grey sentence: the formats and the
 * size are what a customer checks their photo against, and a run-on line of
 * muted prose is the shape of something nobody is expected to read. Not a
 * chip, though, and not bordered — a bordered pill is the shape of a control,
 * and next to a real button it turns the box into a row of look-alike objects
 * with no obvious action. This is a caption; the button is the control.
 *
 * The formats come from the caller's own validation list, not from a string
 * written here, so the note cannot drift from what the input actually accepts.
 */

// One accepted MIME type, as the extensions a customer recognises. `image/*`
// is the wide default of the legacy DragDropFileUpload (a phone photo can
// arrive as any of a dozen types); the three named for it are the ones
// people actually hold, not the whole set. The claim wizard no longer uses
// it — Vista reads jpg/png (and pdf where the slot allows), so it lists
// those explicitly.
const EXTENSIONS_BY_TYPE = {
  "image/*": ["JPG", "PNG", "HEIC"],
  "image/jpeg": ["JPG"],
  "image/jpg": ["JPG"],
  "image/png": ["PNG"],
  "image/heic": ["HEIC"],
  "image/heif": ["HEIC"],
  "image/webp": ["WEBP"],
  "image/gif": ["GIF"],
  "image/bmp": ["BMP"],
  "image/tiff": ["TIFF"],
  "image/avif": ["AVIF"],
  "application/pdf": ["PDF"],
};

/**
 * @param {string[]} types - accepted MIME types, as the input validates them
 * @returns {string[]} the extensions to name, in order, without repeats
 */
export const extensionsFor = (types = []) => {
  const seen = new Set();
  types.forEach((type) => {
    (EXTENSIONS_BY_TYPE[type] || []).forEach((extension) => seen.add(extension));
  });
  return [...seen];
};

const AcceptedFilesNote = ({ types = [], maxSizeMb, className }) => {
  const translate = useLanguageTranslate();
  const extensions = extensionsFor(types);
  if (!extensions.length) return null;

  const listEn = extensions.join(", ");
  const listAr = extensions.join("، ");

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-2 gap-y-1",
        "text-[10px] font-semibold leading-none text-[#5B6871]",
        // Arabic letters join — tracking them apart breaks the word, so the
        // small-caps treatment is latin-only.
        "ltr:uppercase ltr:tracking-[0.09em]",
        className
      )}
    >
      {/* Read as a run of tokens rather than a sentence, so the sentence is
          what goes to a screen reader. */}
      <span className="sr-only">
        {translate(
          `Accepted file types: ${listEn}.${
            maxSizeMb ? ` Maximum size ${maxSizeMb} megabytes per file.` : ""
          }`,
          `أنواع الملفات المقبولة: ${listAr}.${
            maxSizeMb ? ` الحد الأقصى للحجم ${maxSizeMb} ميغابايت لكل ملف.` : ""
          }`
        )}
      </span>
      <span aria-hidden="true">{extensions.join(" · ")}</span>
      {maxSizeMb ? (
        <>
          <span aria-hidden="true" className="text-[#BAC4C9]">
            &bull;
          </span>
          <span aria-hidden="true">
            {translate(`Max ${maxSizeMb}MB`, `${maxSizeMb} ميغابايت كحد أقصى`)}
          </span>
        </>
      ) : null}
    </div>
  );
};

export default AcceptedFilesNote;
